import React, { useState, useCallback } from 'react'
import { useBoQ } from '../../hooks/useBoQ'
import { useTimePeriods, useUpdatePlannedWeight, useCurvaS } from '../../hooks/useCurvaS'
import { EditableCell } from '../table/EditableCell'

interface PlannedWeightFormProps {
  projectId: number
}

export const PlannedWeightForm: React.FC<PlannedWeightFormProps> = ({
  projectId
}) => {
  const { data: boqData } = useBoQ(projectId)
  const { data: periodsData } = useTimePeriods(projectId, 'week')
  const { data: curvaData } = useCurvaS(projectId)
  const updatePlannedWeightMutation = useUpdatePlannedWeight(projectId)

  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 13

  const handleUpdateWeight = useCallback(
    (itemId: number, periodId: number, plannedWeight: number) => {
      updatePlannedWeightMutation.mutate({
        itemId,
        periodId,
        plannedWeight
      })
    },
    [updatePlannedWeightMutation]
  )

  if (!boqData || !periodsData) {
    return <div className="p-4 text-gray-500">Loading...</div>
  }

  const allItems = boqData.categories.flatMap((cat: any) => cat.items)
  const periods = periodsData.periods || []
  const totalPages = Math.ceil(periods.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const displayedPeriods = periods.slice(startIndex, endIndex)

  return (
    <div className="bg-white p-4 rounded-lg shadow">
      <h3 className="text-lg font-semibold mb-4">Input Bobot Rencana (Planned Weight)</h3>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-gray-100">
            <tr>
              <th
                className="border px-2 py-2 text-left font-semibold"
                style={{ width: '250px' }}
              >
                Item Pekerjaan
              </th>
              <th
                className="border px-2 py-2 text-center font-semibold"
                style={{ width: '80px' }}
              >
                Bobot Total
              </th>
              {displayedPeriods.map((period: any) => (
                <th
                  key={period.id}
                  className="border px-1 py-2 text-center font-semibold min-w-[80px]"
                >
                  <div className="text-xs">{period.periodLabel}</div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {allItems.map((item: any) => (
              <tr key={item.id} className="hover:bg-blue-50">
                <td className="border px-2 py-1 text-gray-700" style={{ width: '250px' }}>
                  {item.itemName}
                </td>
                <td className="border px-2 py-1 text-center font-semibold" style={{ width: '80px' }}>
                  {(() => {
                    const totalBobot = curvaData?.rawData
                      .filter((d: any) => d.itemId === item.id)
                      .reduce((sum: number, d: any) => sum + (d.plannedWeight || 0), 0) || 0
                    return totalBobot > 0 ? `${totalBobot.toFixed(2).replace('.', ',')}%` : '—'
                  })()}
                </td>
                {displayedPeriods.map((period: any) => {
                  // Find the planned weight for this item and period
                  const curvaDataPoint = curvaData?.rawData.find(
                    (d: any) => d.itemId === item.id && d.periodId === period.id
                  )
                  const plannedWeight = curvaDataPoint?.plannedWeight || 0
                 
                  return (
                    <td key={`${item.id}-${period.id}`} className="border px-1 py-1">
                      <EditableCell
                        type="number"
                        value={plannedWeight}
                        onChange={(val) =>
                          handleUpdateWeight(item.id, period.id, Number(val))
                        }
                        isLoading={updatePlannedWeightMutation.isPending}
                      />
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-4 flex justify-center items-center space-x-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 bg-gray-200 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 bg-gray-200 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300"
          >
            Next
          </button>
        </div>
      )}

      <div className="mt-4 text-xs text-gray-600">
        <p>💡 Input persentase bobot rencana untuk setiap item per periode</p>
        <p>Bobot Total adalah akumulasi bobot rencana dari semua periode untuk item tersebut</p>
      </div>
    </div>
  )
}